import type { WorkBook } from "xlsx";
import { read } from "xlsx";
import type { CollectionItem } from "../../schema";
import { splitSku } from "./sku";

export type SpreadsheetConversionError = {
	rowIndex: number;
	rowNumber: number;
	message: string;
	data?: Record<string, unknown>;
};

export type SpreadsheetConversionResult = {
	items: CollectionItem[];
	errors: SpreadsheetConversionError[];
	hasErrors: boolean;
};

/**
 * Loads a spreadsheet file and returns a workbook.
 *
 * @param file - Spreadsheet file to load
 * @param onload - Callback invoked with the parsed workbook
 */
export const loadFile = (file: File, onload: (wb: WorkBook) => void) => {
	const reader = new window.FileReader();
	const rABS = !!reader.readAsArrayBuffer;
	if (!file) return;
	reader.onload = ({ target }) => {
		if (!target) return;
		const wb = read(target.result, {
			type: rABS ? "array" : "binary",
		});
		onload(wb);
	};
	if (rABS) reader.readAsArrayBuffer(file);
	else reader.readAsArrayBuffer(file);
};

/**
 * Converts spreadsheet rows into standardized label data.
 * Collects errors for invalid rows and continues processing rather than failing early.
 *
 * @param inputArray - Raw rows from a spreadsheet
 * @returns Result object with valid items and any conversion errors
 */
export const convertSpreadsheet = (inputArray: unknown[]): SpreadsheetConversionResult => {
	const items: CollectionItem[] = [];
	const errors: SpreadsheetConversionError[] = [];

	for (let index = 0; index < inputArray.length; index++) {
		const order = inputArray[index];
		const rowNumber = index + 1;

		// Validate row format
		if (typeof order !== "object" || order === null) {
			errors.push({
				rowIndex: index,
				rowNumber,
				message: "Invalid row format: expected object",
				data: undefined,
			});
			continue;
		}

		const orderObj = order as Record<string, unknown>;
		let firstName: string | undefined;
		let lastName: string | undefined;
		let company: string | undefined;
		const address: {
			street1?: string;
			street2?: string;
			city?: string;
			state?: string;
			zip?: string;
		} = {};
		let message: string | undefined;
		let sku: string = "REG";

		// Parse row data
		for (const [key, value] of Object.entries(orderObj)) {
			const normalizedKey = key.replace(/[_-]+/g, " ").trim().toLowerCase();
			const normalizedValue =
				typeof value === "string"
					? value.trim()
					: value === null || value === undefined
						? ""
						: String(value).trim();

			if (/first\s*name/i.test(normalizedKey)) firstName = normalizedValue || undefined;
			else if (/last\s*name/i.test(normalizedKey)) lastName = normalizedValue || undefined;
			else if (/company/i.test(normalizedKey)) company = normalizedValue || undefined;
			else if (/^name$/i.test(normalizedKey)) {
				const [first, ...lastParts] = String(value).split(" ");
				firstName = first;
				lastName = lastParts.join(" ");
			} else if (/(address|street)\s*2/i.test(normalizedKey))
				address.street2 = normalizedValue || undefined;
			else if (/city/i.test(normalizedKey)) address.city = normalizedValue || undefined;
			else if (/state/i.test(normalizedKey)) address.state = normalizedValue || undefined;
			else if (/(post|zip)/i.test(normalizedKey)) address.zip = normalizedValue || undefined;
			else if (/(address|street)(?:\s*1)?/i.test(normalizedKey))
				address.street1 = normalizedValue || undefined;
			else if (
				/message/i.test(normalizedKey) ||
				/\b(recipient|gift|card)\s*(message|note)\b/i.test(normalizedKey) ||
				/\bnote\b/i.test(normalizedKey)
			) {
				message = normalizedValue || undefined;
			} else if (/sku/i.test(normalizedKey)) sku = normalizedValue || "REG";
		}

		// Validate required address fields
		const missingAddressFields: string[] = [];
		if (!address.street1) missingAddressFields.push("street address");
		if (!address.city) missingAddressFields.push("city");
		if (!address.state) missingAddressFields.push("state");
		if (!address.zip) missingAddressFields.push("postal code");

		// Allow company as fallback for first/last name
		const hasRecipientName = firstName && lastName;
		const hasCompany = company && company.length > 0;

		if (!hasRecipientName && !hasCompany) {
			errors.push({
				rowIndex: index,
				rowNumber,
				message: "Missing recipient name: must provide either first/last name or company name",
				data: orderObj,
			});
			continue;
		}

		if (missingAddressFields.length > 0) {
			errors.push({
				rowIndex: index,
				rowNumber,
				message: `Missing address fields: ${missingAddressFields.join(", ")}`,
				data: orderObj,
			});
			continue;
		}

		// If only company is provided, use it as both first and last name
		if (!hasRecipientName && hasCompany) {
			firstName = company;
			lastName = "";
		}

		try {
			const internalId = crypto.randomUUID().replace(/-/g, "_");
			const { base, addon } = splitSku(sku);

			items.push({
				external: false,
				id: internalId,
				number: String(rowNumber),
				recipient: {
					street1: address.street1!,
					street2: address.street2 || null,
					city: address.city!,
					state: address.state!,
					postalCode: address.zip!,
					country: "US",
					company: company || null,
					firstName: firstName!,
					lastName: lastName!,
				},
				message: message || null,
				addons: addon,
				product: {
					quantity: 1,
					sku: sku || "REG",
					emoji: null,
					body: base,
					additions: addon,
					filling: [],
				},
				graphics: {
					outside: null,
					inside: null,
				},
			});
		} catch (error) {
			const errorMessage = error instanceof Error ? error.message : String(error);
			errors.push({
				rowIndex: index,
				rowNumber,
				message: `Failed to process row: ${errorMessage}`,
				data: orderObj,
			});
		}
	}

	return {
		items,
		errors,
		hasErrors: errors.length > 0,
	};
};

/**
 * Attempts to infer client and collection names from a filename.
 *
 * @param fileName - The file name to inspect
 * @returns Assumed client and collection names
 */
export const assumeValuesFromFileName = (fileName: string) => {
	const nameWithoutExtension = fileName.replace(/\.[^/.]+$/, "");
	const [clientName, collectionName] = nameWithoutExtension
		.split(/\s*\-\s*/)
		.map((str) =>
			str.replace(/(\s*(Bulk|Corporate))|(\s*(Order)\s*)|((\s*\(\s*)*\d+(\s*\)*\s*))/gi, ""),
		);
	return {
		clientName,
		collectionName,
	};
};
